import { Column, FlexBox, Text } from '@dixa/lib-shared-habla';
import { isToolUIPart } from 'ai';

import { type Starter } from 'utils/sidekickStarters';

import { useChatScroll } from '../../../../hooks/useChatScroll';
import { type MimUIMessage } from '../../../../utils/sdk/structuredResponse';
import { MimSidekickToolPartIndicator } from '../mimSidekickToolPartIndicator/mimSidekickToolPartIndicator';

import { MimSidekickChoices } from './components/mimSidekickChoices/mimSidekickChoices';
import { MimSidekickMessage } from './components/mimSidekickMessage/mimSidekickMessage';
import { MimSidekickReasoning } from './components/mimSidekickReasoning/mimSidekickReasoning';

type MimSidekickMessageListProps = {
  messages: MimUIMessage[];
  starters: readonly Starter[];
  onSelectChoice: (text: string) => void;
};

export const MimSidekickMessageList = ({
  messages,
  starters,
  onSelectChoice,
}: MimSidekickMessageListProps): JSX.Element => {
  const containerRef = useChatScroll(messages);

  if (messages.length === 0) {
    if (starters.length === 0) {
      return (
        <Column.Row scrollable>
          <FlexBox alignItems="center" justifyContent="center" height="100%">
            <Text.Paragraph subdued>Ask me about Dixa.</Text.Paragraph>
          </FlexBox>
        </Column.Row>
      );
    }

    return (
      <Column.Row scrollable>
        <FlexBox flexDirection="column" gap="200">
          <Text.Paragraph subdued>What would you like to do?</Text.Paragraph>
          <MimSidekickChoices
            choices={starters.map(({ label }) => label)}
            onSelect={onSelectChoice}
          />
        </FlexBox>
      </Column.Row>
    );
  }

  return (
    <Column.Row ref={containerRef} scrollable>
      <FlexBox flexDirection="column" gap="200">
        {messages.map((message, messageIndex) => {
          const isLastMessage = messageIndex === messages.length - 1;

          return (
            <FlexBox key={message.id} flexDirection="column" gap="100">
              {message.parts.map((part, index) => {
                if (part.type === 'data-message') {
                  return (
                    <MimSidekickMessage
                      key={`${message.id}-${index}`}
                      role={message.role}
                      text={part.data}
                    />
                  );
                }
                if (part.type === 'reasoning') {
                  return (
                    <MimSidekickReasoning
                      key={`${message.id}-${index}`}
                      text={part.text}
                    />
                  );
                }
                if (part.type === 'text') {
                  // Assistant text is the structured output
                  if (message.role === 'assistant') return null;
                  return (
                    <MimSidekickMessage
                      key={`${message.id}-${index}`}
                      role={message.role}
                      text={part.text}
                    />
                  );
                }
                if (
                  part.type === 'data-subagent-open' ||
                  part.type === 'data-subagent-close'
                ) {
                  return null;
                }
                if (part.type === 'data-choices') {
                  if (!isLastMessage) return null;
                  /** Anthropic SDK does not support minItems/maxItems,
                   *  we need to decide here whether to render strucuted output or not.
                   */
                  const choices =
                    part.data.length >= 2 ? part.data.slice(0, 8) : [];
                  return (
                    <MimSidekickChoices
                      key={`${message.id}-${index}`}
                      choices={choices}
                      onSelect={onSelectChoice}
                    />
                  );
                }
                if (isToolUIPart(part)) {
                  return (
                    <MimSidekickToolPartIndicator
                      key={`${message.id}-${index}`}
                      part={part}
                    />
                  );
                }
                return null;
              })}
            </FlexBox>
          );
        })}
      </FlexBox>
    </Column.Row>
  );
};
